import {
  createBreadCrumbsFromArticleGroupsAndArticleGroupSlugs,
  createBreadCrumbsFromCategoryAndCategorySlug,
} from '@/lib/algolia/utils';
import { GetStaticPropsContext, PreviewData } from 'next';
import { ParsedUrlQuery } from 'querystring';
import { configuredSanityClient } from '@/lib/sanity/sanityClient';
import {
  MaterializedAndPopulatedProductCategoryEnrichment,
  populateProductCategoryEnrichmentWithProductUrls,
} from '@/lib/sanity/queries';
import { Breadcrumb } from '@fagmobler/types';
import {
  createCategorySearchProps,
  SearchProps,
} from '@/lib/algolia/createCategorySearchProps';
import { getServerUrlFromContext } from '@/lib/utils/getUrls';
import {
  getCategoriesResult,
  getCatgories,
} from '@/lib/categories/getCategories';
import { populateImageWithPointsWithProducts } from '@/components/contentBlocks/ContentBlocksRenderer';
import {
  MaterializedProductCategoryEnrichment,
  productCategoryEnrichmentQuery,
  productCategoryEnrichmentQueryFromOmniumId,
  SimpleMaterializedArticlePage,
  simpleArticlePageProjection,
} from '@fagmobler/sanity';
import groq from 'groq';

export type ProductCategoryData = {
  breadcrumbs: Breadcrumb[];
  productCategories?: getCategoriesResult;
  sanityProductCategoryEnrichment:
    | MaterializedProductCategoryEnrichment
    | MaterializedAndPopulatedProductCategoryEnrichment
    | null;
  searchProps: SearchProps;
  relatedArticles?: SimpleMaterializedArticlePage[];
};

export const getProductCategoryData = async ({
  context,
  slugs,
  categoryType,
}: {
  context: GetStaticPropsContext<ParsedUrlQuery, PreviewData>;
  slugs: string[];
  categoryType: 'categories' | 'articleGroup';
}) => {
  const isRoot = slugs.filter(Boolean).length === 0;
  // some GTM script is trying to access this url
  if (slugs[0] === 'null' || slugs[0] === 'sdk') return null;

  let rootPath = 'rom';
  if (categoryType === 'articleGroup') rootPath = 'produkter';
  let omniumCategoryId = categoryType == 'articleGroup' ? 'AG' : 'ROM';

  const serverUrl = getServerUrlFromContext(context, categoryType);

  const searchProps = await createCategorySearchProps({
    rootPath,
    slugs,
    categoryType,
    serverUrl,
  });

  const {
    categories,
    categorySlugs,
    articleGroups,
    articleGroupSlugs,
    categorySlugsRootPath,
  } = searchProps;

  // Get Product Category Enrichment from current category string

  const sanityProductCategoryEnrichment = categorySlugsRootPath
    ? await configuredSanityClient.fetch<MaterializedProductCategoryEnrichment>(
        productCategoryEnrichmentQuery,
        {
          algoliaProductCategory: categorySlugsRootPath,
        }
      )
    : await configuredSanityClient.fetch<MaterializedProductCategoryEnrichment>(
        productCategoryEnrichmentQueryFromOmniumId,
        {
          omniumId: omniumCategoryId,
        }
      );

  if (sanityProductCategoryEnrichment?.omniumId) {
    omniumCategoryId = sanityProductCategoryEnrichment.omniumId;
  }

  const contentBlocks = sanityProductCategoryEnrichment?.contentBlocks;
  const priorityContentBlocks =
    sanityProductCategoryEnrichment?.priorityContentBlocks;

  if (contentBlocks) {
    for (const block of contentBlocks) {
      if ('imageWithPoints' in block && block.imageWithPoints) {
        const populated = await populateImageWithPointsWithProducts(
          block.imageWithPoints
        );
        // @ts-ignore
        block.imageWithPoints = populated;
      }
    }
  }

  if (priorityContentBlocks) {
    for (const block of priorityContentBlocks) {
      if ('imageWithPoints' in block && block.imageWithPoints) {
        const populated = await populateImageWithPointsWithProducts(
          block.imageWithPoints
        );
        // @ts-ignore
        block.imageWithPoints = populated;
      }
    }
  }

  // Return null (404) if no cateogry
  if (!categorySlugsRootPath && !isRoot) {
    console.log('No categorySlugsRootPath found', {
      categorySlugsRootPath,
      isRoot,
    });
    return null;
  }

  let breadcrumbs: Breadcrumb[] = [];

  if (!categoryType) {
    throw new Error('categoryType is required');
  }

  if (categoryType === 'categories') {
    breadcrumbs = createBreadCrumbsFromCategoryAndCategorySlug({
      categories,
      categorySlugs,
      slugs,
      categoryType,
    });
  }

  if (categoryType === 'articleGroup') {
    breadcrumbs = createBreadCrumbsFromArticleGroupsAndArticleGroupSlugs({
      articleGroups,
      articleGroupSlugs,
      slugs,
      categoryType,
    });
  }

  const productCategories = await getCatgories(omniumCategoryId);

  const materializedAndPopulatedProductCategoryEnrichment =
    sanityProductCategoryEnrichment
      ? await populateProductCategoryEnrichmentWithProductUrls(
          sanityProductCategoryEnrichment
        )
      : null;

  const relatedArticles = await configuredSanityClient.fetch<
    SimpleMaterializedArticlePage[]
  >(
    groq`
    *[(_type == "page" || _type == "article") && references($id)] {
      ${simpleArticlePageProjection}
    }
    `,
    { id: materializedAndPopulatedProductCategoryEnrichment?._id }
  );

  const data: ProductCategoryData = {
    breadcrumbs: breadcrumbs || [{ name: 'Rom', href: '/rom' }],
    sanityProductCategoryEnrichment:
      materializedAndPopulatedProductCategoryEnrichment,
    productCategories,
    relatedArticles,
    searchProps: {
      ...searchProps,
      showCategories: true,
    },
  };
  return data;
};
